import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ArrowRight, PiggyBank } from "lucide-react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

/** Hook — fetches annual-upsell eligibility, refetching whenever auth state changes. */
export function useAnnualUpsell() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const refresh = useCallback(async () => {
    if (!user) { setStatus({ eligible: false }); return; }
    try {
      const { data } = await api.get("/billing/annual-upsell");
      setStatus(data);
    } catch (_e) {
      setStatus({ eligible: false });
    }
  }, [user]);
  useEffect(() => { refresh(); }, [refresh]);
  return { status, refresh };
}

/** In-app banner — shows globally on /billing & /pricing once eligible. */
export function AnnualUpsellBanner({ status, onDismiss }) {
  if (!status?.eligible) return null;
  const savings = status.savings_amount != null ? `$${Math.round(status.savings_amount)}` : "~17%";
  const months = status.months_in || 10;
  return (
    <div
      className="rounded-2xl border-2 border-slate-900 bg-gradient-to-r from-slate-900 to-indigo-900 text-white px-5 py-4 flex items-center gap-4 flex-wrap shadow-lg shadow-indigo-900/10"
      data-testid="annual-upsell-banner"
    >
      <div className="flex-none w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
        <PiggyBank className="w-5 h-5 text-emerald-300" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-semibold">{months} months on Pro</div>
        <div className="font-outfit text-base sm:text-lg font-semibold mt-0.5">
          You've earned the annual rate — save <span className="text-emerald-300">{savings}/year</span>.
        </div>
      </div>
      <Button asChild className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="annual-upsell-banner-cta">
        <Link to="/billing?upsell=annual">Switch to annual <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
      </Button>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-slate-300 hover:text-white p-1"
          data-testid="annual-upsell-banner-dismiss"
          aria-label="Dismiss"
        ><X className="w-4 h-4" /></button>
      )}
    </div>
  );
}

/** One-time modal — pops once after eligibility, persists dismissal server-side. */
export function AnnualUpsellModal({ status, onDismissed }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (status?.eligible && status?.show_modal) {
      // tiny delay so the page renders first
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [status]);

  const handleClose = async () => {
    setOpen(false);
    try {
      await api.post("/billing/annual-upsell/dismiss");
      onDismissed?.();
    } catch (_e) { /* non-fatal */ }
  };

  if (!status?.eligible) return null;
  const savings = status.savings_amount != null ? `$${Math.round(status.savings_amount)}` : "~17%";
  const months = status.months_in || 10;
  const annualPrice = status.annual_amount;
  const monthlyX12 = status.monthly_x12_amount;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md" data-testid="annual-upsell-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 w-fit mb-3">
            <Sparkles className="w-3 h-3 text-amber-600" />
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-700">{months} months on Pro</span>
          </div>
          <DialogTitle className="font-outfit text-2xl font-bold leading-tight">
            You've earned the <span className="text-emerald-600">annual rate</span>.
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 mt-1">
            Switch to annual Pro and lock in your access for the next year — roughly two months free vs. your current monthly plan.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 my-5">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Stay monthly</div>
            <div className="font-outfit text-2xl font-bold mt-1">${monthlyX12 ? Math.round(monthlyX12) : 348}<span className="text-xs font-normal text-slate-400">/yr</span></div>
          </div>
          <div className="rounded-lg border-2 border-slate-900 bg-white p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-900 font-semibold">Annual</div>
            <div className="font-outfit text-2xl font-bold mt-1">${annualPrice ? Math.round(annualPrice) : 290}<span className="text-xs font-normal text-slate-400">/yr</span></div>
            <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">Save {savings}</div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button asChild className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="annual-upsell-modal-cta">
            <Link to="/billing?upsell=annual" onClick={handleClose}>Switch to annual <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
          </Button>
          <Button variant="ghost" onClick={handleClose} data-testid="annual-upsell-modal-dismiss">
            Maybe later
          </Button>
        </div>

        <p className="text-[10px] text-slate-400 mt-3 text-center">
          One-click switch. Your next monthly invoice is prorated against the annual cost.
        </p>
      </DialogContent>
    </Dialog>
  );
}
